/**
 * Test Store - Zustand state management for test/quiz functionality
 * Validates: Requirements 6.3, 6.4, 6.5, 6.10, 12.8
 */

import { create } from 'zustand';
import {
  loadTestData,
  saveTestAnswer,
  removeTestAnswer,
} from '../utils/localStorage';
import { serverNow } from '../utils/serverTime';
import { AnswerRejectedError } from '../api/realtimeQuestion';

/** 回答送信関数の型（useRealtimeQuestion から注入される） */
type SubmitAnswerFn = (
  data: {
    question_id: number; // 不変の問題ID
    choice_num: number;
    choice_internal_id: string; // 選択肢の一意識別子（必須）
    user_id: string;
    answer_time_ms: number;
  },
  options?: { signal?: AbortSignal },
) => Promise<void>;

/**
 * 問題が現在も回答受付中かをサーバーに確認する関数の型（LecturePage から注入される）。
 * true: 受付中 / false: 締切済み / null: 判定不能
 */
type CheckAnswerableFn = (questionId: number) => Promise<boolean | null>;

/**
 * サーバーに受理済みの回答（choice_num）を確認する関数の型（LecturePage から注入される）。
 * number: 受理済みの choice_num（0=未回答として受理済み） / null: 記録なし・判定不能
 */
type FetchServerAnswerFn = (questionId: number) => Promise<number | null>;

/** 回答送信の状態（問題ごと） */
export type AnswerSendState = 'sending' | 'confirmed' | 'failed';

/** サーバー側（onPublish）の締切後受付バッファと同値 */
const ANSWER_DEADLINE_BUFFER_MS = 2000;
/** リトライ間隔（指数バックオフ）。以降は最後の値を使用 */
const RETRY_DELAYS_MS = [500, 1000, 2000, 4000];
/** 締切情報がない場合の最大試行回数 */
const MAX_SEND_ATTEMPTS = 8;
/**
 * 未回答送信（choice_num=0）で締切時刻が不明な場合のリトライ最大時間。
 * 締切後の未回答送信はサーバー側で受付拒否されるため、無期限に
 * オンライン復帰を待ち続けても意味がない（受講生B-11）。
 */
const UNANSWERED_RETRY_WINDOW_MS = 60_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * オンライン復帰（online イベント）または受付期限到達まで待つ。
 * オンラインに復帰したら 'online'、期限に到達したら 'deadline' を返す。
 */
function waitForOnlineOrDeadline(deadlineMs?: number): Promise<'online' | 'deadline'> {
  return new Promise((resolve) => {
    let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
    const onOnline = () => {
      if (deadlineTimer !== undefined) clearTimeout(deadlineTimer);
      resolve('online');
    };
    if (deadlineMs !== undefined) {
      // 締切時刻はサーバー時刻基準（scheduled_close_time）のため serverNow() と比較する
      const remainMs = deadlineMs - serverNow();
      if (remainMs <= 0) {
        resolve('deadline');
        return;
      }
      deadlineTimer = setTimeout(() => {
        window.removeEventListener('online', onOnline);
        resolve('deadline');
      }, remainMs);
    }
    window.addEventListener('online', onOnline, { once: true });
  });
}

/**
 * sendWithRetry の結果種別
 * - success: 送信成功
 * - closed: 締切到達（期限超過、またはサーバー確認で締切済みと判明）により断念
 * - exhausted: リトライ上限到達により断念（締切前）
 */
type SendOutcome = 'success' | 'closed' | 'exhausted';

/**
 * 指数バックオフ付きで送信をリトライする。
 *
 * - deadlineMs が指定された場合:
 *   - 各試行前に期限超過をチェックし、超過していたら送信せずに打ち切る（'closed'）
 *   - 進行中の fetch も期限到達時に AbortSignal で中断する
 *     （期限後に送信が遅れて届き、サーバー側で拒否される回答が発生するのを防ぐ）
 * - isStillOpen が指定された場合、失敗後の再送信前にサーバーへ回答受付状態を確認し、
 *   締切済み（false）なら送信せずに打ち切る（'closed'）。
 *   クライアントが締切予定時刻を保持していないケースや手動締切にも対応するため
 * - オフライン中の失敗は試行回数を消費せず、オンライン復帰（または期限到達）まで待つ
 *   （オンライン復帰時に確実に再送信するため）
 */
async function sendWithRetry(
  send: (signal: AbortSignal) => Promise<void>,
  options?: {
    deadlineMs?: number;
    isStillOpen?: () => Promise<boolean | null>;
  },
): Promise<SendOutcome> {
  const deadlineMs = options?.deadlineMs;
  const isStillOpen = options?.isStillOpen;

  let attempt = 0;
  let hasFailed = false;
  while (attempt < MAX_SEND_ATTEMPTS) {
    if (deadlineMs !== undefined && serverNow() > deadlineMs) {
      console.warn('[TestStore] 受付期限超過のため送信を打ち切り');
      return 'closed';
    }

    // 失敗後の再送信前に、サーバー側の回答受付状態を確認する
    // （切断中に締め切られた場合、クライアントの締切予定時刻だけでは検知できないため）
    if (hasFailed && isStillOpen) {
      try {
        const open = await isStillOpen();
        if (open === false) {
          console.warn('[TestStore] サーバー確認で締切済みのため送信を打ち切り');
          return 'closed';
        }
        // null（判定不能）は受付中として扱い、期限・試行回数ベースの打ち切りに委ねる
      } catch {
        // 確認自体の失敗は無視して送信を試行する
      }
    }

    // 期限到達時に進行中の送信を中断するための AbortController
    const controller = new AbortController();
    const abortTimer = deadlineMs !== undefined
      ? setTimeout(() => controller.abort(), Math.max(0, deadlineMs - serverNow()))
      : undefined;

    try {
      await send(controller.signal);
      return 'success';
    } catch (err) {
      hasFailed = true;

      // サーバー（onPublish Lambda）が業務判定で拒否した場合（受講生B-5）:
      // - 締切済み・未出題が理由なら、同じ回答を再送信しても受理されないため打ち切る
      // - それ以外（理由不明・一時エラー）は通常の失敗として扱い、
      //   次の試行前の isStillOpen チェック（サーバー確認）に委ねる
      if (err instanceof AnswerRejectedError) {
        if (/closed|not open/i.test(err.message)) {
          console.warn('[TestStore] サーバーが回答を拒否（締切済み）したため送信を打ち切り:', err.message);
          return 'closed';
        }
      }

      // オフライン中は試行回数を消費せず、オンライン復帰を待って再送信する
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        console.warn('[TestStore] オフラインのため、オンライン復帰を待って再送信します:', err);
        const result = await waitForOnlineOrDeadline(deadlineMs);
        if (result === 'deadline') {
          console.warn('[TestStore] オンライン復帰前に受付期限に到達したため送信を打ち切り');
          return 'closed';
        }
        continue;
      }

      attempt++;
      if (attempt >= MAX_SEND_ATTEMPTS) {
        console.error('[TestStore] 送信リトライ上限に到達:', err);
        return 'exhausted';
      }
      const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length) - 1] ?? 4000;
      if (deadlineMs !== undefined && serverNow() + delay > deadlineMs) {
        console.error('[TestStore] 受付期限を超えるため送信リトライを打ち切り:', err);
        return 'closed';
      }
      console.warn(`[TestStore] 送信失敗（${attempt}回目）。${delay}ms後に再試行:`, err);
      await sleep(delay);
    } finally {
      if (abortTimer !== undefined) clearTimeout(abortTimer);
    }
  }
  return 'exhausted';
}

interface TestStore {
  // State
  currentQuestionIndex: number;
  selectedChoices: Record<number, number | null>; // questionId -> choiceId(null=未回答)
  submittedQuestions: Set<number>;
  timerActive: boolean;
  remainingTime: number;
  lectureId: string | null;
  /** 問題の回答開始時刻（question.id -> timestamp） */
  answerStartTimes: Record<number, number>;
  /** WebSocket 回答送信関数（接続時に注入） */
  realtimeSubmitFn: SubmitAnswerFn | null;
  /** 回答受付状態のサーバー確認関数（LecturePage から注入） */
  checkAnswerableFn: CheckAnswerableFn | null;
  /** サーバー受理済み回答の確認関数（LecturePage から注入。STU-5対応） */
  fetchServerAnswerFn: FetchServerAnswerFn | null;
  /** 現在のユーザーID */
  userId: string;
  /** エラーメッセージ（回答送信失敗時など） */
  errorMessage: string | null;
  /** 問題ごとの回答送信状態（questionId -> 状態）。sending 中はUIで送信ボタンを無効化する */
  answerSendStates: Record<number, AnswerSendState>;
  /** 問題ごとの締切時刻 epoch ms（questionId -> scheduled_close_time）。リトライ打ち切り判定用 */
  questionDeadlines: Record<number, number>;
  /** 問題ごとの制限時間 ms（questionId -> time_limit * 1000）。締切超過時の未回答送信の回答時間用 */
  questionTimeLimits: Record<number, number>;
  /**
   * 問題ごとの送信世代（questionId -> 世代番号）。resetQuestion（テスト中止等）で
   * インクリメントし、進行中の送信リトライが古い世代の結果を反映しないようにする（受講生B-8）
   */
  answerEpochs: Record<number, number>;
  /** ストア全体の世代。reset()（講義切替等）でインクリメントする（受講生B-8） */
  storeEpoch: number;

  // Actions
  setLectureId: (lectureId: string) => void;
  setCurrentQuestion: (index: number) => void;
  selectChoice: (questionId: number, choiceId: number) => void;
  submitAnswer: (questionId: number) => Promise<void>;
  submitUnanswered: (questionId: number) => Promise<void>;
  /** 締切超過で回答を断念した際に「未回答」（テスト不参加扱い）を送信する（内部用） */
  sendUnansweredAsGiveUp: (questionId: number) => Promise<void>;
  /** sendUnansweredAsGiveUp の実装本体。直接呼ばずガード付きの sendUnansweredAsGiveUp を使用すること */
  sendUnansweredAsGiveUpInner: (questionId: number) => Promise<void>;
  /** 問題の締切時刻（epoch ms）と制限時間（ms）を設定する */
  setQuestionDeadline: (questionId: number, deadlineMs: number, timeLimitMs?: number) => void;
  startTimer: (timeLimit: number) => void;
  stopTimer: () => void;
  tickTimer: () => void;
  loadAnswersFromLocalStorage: (lectureId: string, userId: string) => void;
  loadAnswersFromApi: (
    answers: Array<{ question_id: number; choice_num: number }>,
    questionIdMap: Record<number, number>
  ) => void;
  /**
   * サーバーの回答履歴を正として、ストアと localStorage の回答を突き合わせる。
   * サーバーに存在しない問題のローカル回答（中止で削除された旧回答など）を削除した上で、
   * サーバーの回答を反映する。
   */
  reconcileAnswersWithServer: (
    answers: Array<{ question_id: number; choice_num: number }>,
    questionIdMap: Record<number, number>,
    lectureQuestionIds: number[],
  ) => void;
  saveAnswerToLocalStorage: (lectureId: string, userId: string, questionId: number, choiceId: number | null) => void;
  isQuestionSubmitted: (questionId: number) => boolean;
  getSelectedChoice: (questionId: number) => number | null;
  clearAnswers: () => void;
  /** テスト中止時に特定問題の送信状態・選択をリセットする（再回答を許可） */
  resetQuestion: (questionId: number) => void;
  reset: () => void;
  /** 問題開始時に回答計測を開始する */
  startAnswerTimer: (questionId: number) => void;
  /**
   * リロード/再同期での復元時に回答開始時刻を復元する（受講生B-2）。
   * QuestionOpened を経由しない復元では answerStartTimes が未設定のまま
   * answer_time_ms=0 で送信されてしまうため、出題時刻（opened_at）から逆算した
   * 開始時刻を設定する。既に開始時刻がある場合は上書きしない。
   */
  restoreAnswerStartTime: (questionId: number, startTimeMs: number) => void;
  /** WebSocket 回答送信関数を注入する */
  setRealtimeSubmitFn: (fn: SubmitAnswerFn | null) => void;
  /** 回答受付状態のサーバー確認関数を注入する */
  setCheckAnswerableFn: (fn: CheckAnswerableFn | null) => void;
  /** サーバー受理済み回答の確認関数を注入する（STU-5対応） */
  setFetchServerAnswerFn: (fn: FetchServerAnswerFn | null) => void;
  /** ユーザーIDを設定する */
  setUserId: (userId: string) => void;
  /** エラーメッセージを設定する */
  setErrorMessage: (message: string | null) => void;
  /** 問題番号を questionId から取得するマッピング */
  questionNumMap: Record<number, number>; // questionId -> questionNum
  /** 表示用通し番号を questionId から取得するマッピング（format_type=questionの場合のみ） */
  displayNumberMap: Record<number, number | null>; // questionId -> displayNumber
  setQuestionNumMap: (questionId: number, questionNum: number, displayNumber?: number | null) => void;
  /** ローカルストレージから特定問題の回答を復元する（リロード時用） */
  restoreAnswerFromLocalStorage: (lectureId: string, userId: string, questionId: number) => void;
  /** 問題の選択肢情報マップ（questionId -> 選択肢配列） */
  questionChoicesMap: Record<number, Array<{ id: number; internal_id: string }>>;
  /** 問題の選択肢情報を設定する */
  setQuestionChoices: (questionId: number, choices: Array<{ id: number; internal_id: string }>) => void;
}

const initialState = {
  currentQuestionIndex: 0,
  selectedChoices: {} as Record<number, number | null>,
  submittedQuestions: new Set<number>(),
  timerActive: false,
  remainingTime: 0,
  lectureId: null as string | null,
  answerStartTimes: {} as Record<number, number>,
  realtimeSubmitFn: null as SubmitAnswerFn | null,
  checkAnswerableFn: null as CheckAnswerableFn | null,
  fetchServerAnswerFn: null as FetchServerAnswerFn | null,
  userId: '',
  errorMessage: null as string | null,
  answerSendStates: {} as Record<number, AnswerSendState>,
  questionDeadlines: {} as Record<number, number>,
  questionTimeLimits: {} as Record<number, number>,
  questionNumMap: {} as Record<number, number>,
  displayNumberMap: {} as Record<number, number | null>,
  questionChoicesMap: {} as Record<number, Array<{ id: number; internal_id: string }>>,
  answerEpochs: {} as Record<number, number>,
  storeEpoch: 0,
};

/**
 * 送信開始時に記録した世代と現在の世代が一致するかを確認する（受講生B-8）。
 * resetQuestion（テスト中止→再出題）や reset（講義切替）を跨いだ
 * 古い送信結果がストアに反映されるのを防ぐ。
 */
function isSameEpoch(
  state: { storeEpoch: number; answerEpochs: Record<number, number> },
  questionId: number,
  storeEpoch: number,
  answerEpoch: number,
): boolean {
  return state.storeEpoch === storeEpoch && (state.answerEpochs[questionId] ?? 0) === answerEpoch;
}

/** エラーメッセージの自動クリアタイマー（古いタイマーが新しいメッセージを消すのを防ぐ） */
let errorMessageTimer: ReturnType<typeof setTimeout> | undefined;

/** sendUnansweredAsGiveUp の多重実行ガード（実行中の questionId を保持。STU-L2対応） */
const giveUpInFlight = new Set<number>();

export const useTestStore = create<TestStore>((set, get) => ({
  ...initialState,

  setLectureId: (lectureId: string) => {
    set({ lectureId });
  },

  setCurrentQuestion: (index: number) => {
    set({ currentQuestionIndex: index });
  },

  selectChoice: (questionId: number, choiceId: number) => {
    set((state) => ({
      selectedChoices: {
        ...state.selectedChoices,
        [questionId]: choiceId,
      },
    }));
  },

  submitAnswer: async (questionId: number) => {
    const {
      selectedChoices,
      lectureId,
      realtimeSubmitFn,
      userId,
      answerStartTimes,
      questionChoicesMap,
      answerSendStates,
      questionDeadlines,
    } = get();

    // 送信中の二重実行ガード（連打・SP/PCレイアウト同時押下対策）
    if (answerSendStates[questionId] === 'sending') {
      return;
    }

    const choiceId = selectedChoices[questionId];
    if (choiceId === undefined || choiceId === null || !lectureId || !userId) {
      return;
    }

    // 送信手段がない場合（アーカイブ・試用ユーザー等）は従来どおりローカルにのみ確定する
    if (!realtimeSubmitFn) {
      saveTestAnswer(lectureId, userId, questionId, choiceId);
      set((state) => ({
        submittedQuestions: new Set([...state.submittedQuestions, questionId]),
      }));
      return;
    }

    // 回答時間は初回送信時に一度だけ算出し、リトライで水増ししない
    const startTime = answerStartTimes[questionId];
    const answerTimeMs = startTime ? Date.now() - startTime : 0;

    // 問題の選択肢情報から choice_internal_id を取得
    const questionChoices = questionChoicesMap[questionId] || [];
    const selectedChoice = questionChoices.find(c => c.id === choiceId);
    const choiceInternalId = selectedChoice?.internal_id || '';

    if (!choiceInternalId) {
      console.error('[TestStore] choice_internal_id が見つかりません:', { questionId, choiceId, questionChoices });
    }

    set((state) => ({
      answerSendStates: { ...state.answerSendStates, [questionId]: 'sending' },
    }));

    // 送信開始時点の世代を記録（送信中の中止→再出題を跨いだ古い結果の反映を防ぐ。受講生B-8）
    const epochAtSend = get().answerEpochs[questionId] ?? 0;
    const storeEpochAtSend = get().storeEpoch;

    // サーバー側の受付期限（scheduled_close_time + バッファ）までリトライする
    const deadline = questionDeadlines[questionId];
    const retryDeadlineMs = deadline !== undefined ? deadline + ANSWER_DEADLINE_BUFFER_MS : undefined;

    // 失敗後の再送信前に、サーバーへ回答受付状態を確認するためのコールバック
    // （締切予定時刻がストアにない場合や手動締切の場合、期限チェックだけでは検知できないため）
    const checkFn = get().checkAnswerableFn;
    const isStillOpen = checkFn ? () => checkFn(questionId) : undefined;

    const outcome = await sendWithRetry(
      (signal) => realtimeSubmitFn({
        question_id: questionId,
        choice_num: choiceId,
        choice_internal_id: choiceInternalId,
        user_id: userId,
        answer_time_ms: answerTimeMs,
      }, { signal }),
      {
        ...(retryDeadlineMs !== undefined ? { deadlineMs: retryDeadlineMs } : {}),
        ...(isStillOpen ? { isStillOpen } : {}),
      },
    );

    // 送信中に resetQuestion（テスト中止）や reset（講義切替）が実行された場合、
    // この結果は古い世代のものなので反映しない（受講生B-8）
    if (!isSameEpoch(get(), questionId, storeEpochAtSend, epochAtSend)) {
      console.warn('[TestStore] 送信中に問題がリセットされたため結果を破棄:', { questionId, outcome });
      return;
    }

    if (outcome === 'success') {
      // サーバー受理を確認してから「回答済み」を確定・localStorage に保存する
      saveTestAnswer(lectureId, userId, questionId, choiceId);
      set((state) => ({
        submittedQuestions: new Set([...state.submittedQuestions, questionId]),
        answerSendStates: { ...state.answerSendStates, [questionId]: 'confirmed' },
      }));
    } else if (outcome === 'closed') {
      // 未回答へ切り替える前に、サーバーに受理済みの回答がないか確認する（STU-5対応）。
      // 「送信自体はサーバーに届いて受理されたが、応答を受け取れずリトライ→締切到達」の
      // ケースでは、サーバー側に正しい回答が記録済みのため、ローカルを「未回答」で
      // 上書きせずサーバーの回答を復元する（サーバーが正）
      const fetchServerAnswer = get().fetchServerAnswerFn;
      if (fetchServerAnswer) {
        try {
          const serverChoiceNum = await fetchServerAnswer(questionId);
          // 確認中にリセットされた場合は結果を反映しない（受講生B-8）
          if (!isSameEpoch(get(), questionId, storeEpochAtSend, epochAtSend)) {
            console.warn('[TestStore] サーバー回答確認中に問題がリセットされたため結果を破棄:', { questionId });
            return;
          }
          if (serverChoiceNum !== null && serverChoiceNum > 0) {
            console.log('[TestStore] サーバーに受理済みの回答を確認。ローカルへ復元します:', { questionId, serverChoiceNum });
            saveTestAnswer(lectureId, userId, questionId, serverChoiceNum);
            set((state) => ({
              selectedChoices: { ...state.selectedChoices, [questionId]: serverChoiceNum },
              submittedQuestions: new Set([...state.submittedQuestions, questionId]),
              answerSendStates: { ...state.answerSendStates, [questionId]: 'confirmed' },
            }));
            return;
          }
        } catch {
          // 確認自体の失敗は無視して従来どおり未回答として送信する
        }
      }

      // 締切到達で回答を断念した場合（オフラインのまま締切を迎えた場合など）は、
      // 選択済みの回答ではなく「未回答」に切り替えて送信する（テスト不参加扱い）。
      // 締切後の回答はサーバー側で受付拒否されるため、遅れて送信しても記録されない。
      console.warn('[TestStore] 締切のため回答を断念し、未回答として送信します:', { questionId, choiceId });
      await get().sendUnansweredAsGiveUp(questionId);
    } else {
      // 締切前にリトライ上限へ到達した場合は failed にして再送信を可能にする。
      // 選択状態を未選択に戻すことで、（同一選択肢を含む）どの選択肢のクリックでも
      // onChange が発火し、再選択＝再送信できる（1クリック回答のリトライ手段）。
      // エラーは問題カード内のメッセージ表示（TestQuestion）で通知するため、モーダルは表示しない
      console.error('[TestStore] 回答送信を打ち切りました:', { questionId, choiceId });
      set((state) => {
        const { [questionId]: _removedChoice, ...restChoices } = state.selectedChoices;
        return {
          selectedChoices: restChoices,
          answerSendStates: { ...state.answerSendStates, [questionId]: 'failed' },
        };
      });
    }
  },

  sendUnansweredAsGiveUp: async (questionId: number) => {
    const { lectureId, realtimeSubmitFn, userId } = get();
    if (!lectureId || !userId || !realtimeSubmitFn) {
      return;
    }

    // 多重実行ガード（STU-L2対応）。同一問題への並行実行は
    // 未回答送信のリトライループを重複させるだけなのでスキップする
    if (giveUpInFlight.has(questionId)) {
      return;
    }
    giveUpInFlight.add(questionId);
    try {
      await get().sendUnansweredAsGiveUpInner(questionId);
    } finally {
      giveUpInFlight.delete(questionId);
    }
  },

  sendUnansweredAsGiveUpInner: async (questionId: number) => {
    const { lectureId, realtimeSubmitFn, userId, questionTimeLimits, answerStartTimes, questionDeadlines } = get();
    if (!lectureId || !userId || !realtimeSubmitFn) {
      return;
    }

    // 送信開始時点の世代を記録（受講生B-8）
    const epochAtSend = get().answerEpochs[questionId] ?? 0;
    const storeEpochAtSend = get().storeEpoch;

    // ローカルも「未回答」に戻す（localStorage の回答も null で上書き）
    saveTestAnswer(lectureId, userId, questionId, null);
    set((state) => ({
      selectedChoices: { ...state.selectedChoices, [questionId]: null },
      submittedQuestions: new Set([...state.submittedQuestions, questionId]),
    }));

    // 回答時間は実際の経過時間ではなくテスト制限時間とする
    // （通常のテスト開始後に何も回答せず送信される未回答と同じ扱い）
    const timeLimitMs = questionTimeLimits[questionId];
    const startTime = answerStartTimes[questionId];
    const answerTimeMs = timeLimitMs ?? (startTime ? Date.now() - startTime : 0);

    // 締切+バッファ超過後の未回答はサーバー側で受理されない（不参加扱い、S1/G2対応）。
    // 送信自体は行うが、拒否されても実害はない（ローカル表示は「未回答」のまま）。
    // 期限を渡してオフライン時の無期限待機を防ぐ（受講生B-11）
    const deadline = questionDeadlines[questionId];
    const retryDeadlineMs = deadline !== undefined
      ? deadline + ANSWER_DEADLINE_BUFFER_MS
      : serverNow() + UNANSWERED_RETRY_WINDOW_MS;
    const outcome = await sendWithRetry((signal) => realtimeSubmitFn({
      question_id: questionId,
      choice_num: 0,
      choice_internal_id: '',
      user_id: userId,
      answer_time_ms: answerTimeMs,
    }, { signal }), { deadlineMs: retryDeadlineMs });

    // 送信中にリセットされた場合は結果を反映しない（受講生B-8）
    if (!isSameEpoch(get(), questionId, storeEpochAtSend, epochAtSend)) {
      console.warn('[TestStore] 送信中に問題がリセットされたため未回答送信結果を破棄:', { questionId, outcome });
      return;
    }

    // closed（期限到達・サーバー拒否）は想定内の打ち切りのため失敗扱いにしない
    // （締切後の未回答はサーバーで不参加として扱われ、ローカル表示も「未回答」のまま）
    const succeeded = outcome === 'success' || outcome === 'closed';
    set((state) => ({
      answerSendStates: { ...state.answerSendStates, [questionId]: succeeded ? 'confirmed' : 'failed' },
    }));
    if (!succeeded) {
      console.error('[TestStore] 締切超過後の未回答送信を打ち切りました:', { questionId });
    }
  },

  submitUnanswered: async (questionId: number) => {
    const { lectureId, realtimeSubmitFn, userId, answerStartTimes, submittedQuestions, answerSendStates, questionDeadlines } = get();

    // 既に回答を送信済みの場合は未回答データを送信しない
    if (submittedQuestions.has(questionId)) {
      return;
    }
    // 回答送信がリトライ中の場合もスキップする
    // （締切バッファ内に成功しうる回答を、後着の未回答(choice_num=0)が上書きする事故を防ぐ）
    if (answerSendStates[questionId] === 'sending') {
      return;
    }
    if (!lectureId || !userId) {
      return;
    }

    saveTestAnswer(lectureId, userId, questionId, null);

    set((state) => ({
      selectedChoices: {
        ...state.selectedChoices,
        [questionId]: null,
      },
      submittedQuestions: new Set([...state.submittedQuestions, questionId]),
    }));

    // 未回答は choice_num=0 としてバックエンドに送信する
    // （サーバー側の受付は締切+バッファまで。期限を渡してオフライン時の無期限待機を防ぐ。受講生B-11）
    if (realtimeSubmitFn) {
      const startTime = answerStartTimes[questionId];
      const answerTimeMs = startTime ? Date.now() - startTime : 0;

      const deadline = questionDeadlines[questionId];
      const retryDeadlineMs = deadline !== undefined
        ? deadline + ANSWER_DEADLINE_BUFFER_MS
        : serverNow() + UNANSWERED_RETRY_WINDOW_MS;

      // question_id のみを送信（バックエンドは question_id のみで動作）
      // 未回答の場合は choice_internal_id を空文字列で送信
      const outcome = await sendWithRetry((signal) => realtimeSubmitFn({
        question_id: questionId,
        choice_num: 0,
        choice_internal_id: '',
        user_id: userId,
        answer_time_ms: answerTimeMs,
      }, { signal }), { deadlineMs: retryDeadlineMs });

      // closed（期限到達・サーバー拒否）は想定内の打ち切りのためエラー表示しない
      if (outcome === 'exhausted') {
        console.error('[TestStore] 未回答データの送信を打ち切りました:', { questionId });
        get().setErrorMessage('未解答データの送信に失敗しました。');
      }
    }
  },

  setQuestionDeadline: (questionId: number, deadlineMs: number, timeLimitMs?: number) => {
    set((state) => ({
      questionDeadlines: {
        ...state.questionDeadlines,
        [questionId]: deadlineMs,
      },
      ...(timeLimitMs !== undefined && timeLimitMs > 0
        ? {
            questionTimeLimits: {
              ...state.questionTimeLimits,
              [questionId]: timeLimitMs,
            },
          }
        : {}),
    }));
  },

  startTimer: (timeLimit: number) => {
    set({
      timerActive: true,
      remainingTime: timeLimit,
    });
  },

  stopTimer: () => {
    set({
      timerActive: false,
      remainingTime: 0,
    });
  },

  tickTimer: () => {
    const { remainingTime, timerActive } = get();
    
    if (!timerActive || remainingTime <= 0) {
      return;
    }

    const newTime = remainingTime - 1;
    set({ remainingTime: newTime });

    // Auto-submit when timer reaches 0
    if (newTime === 0) {
      set({ timerActive: false });
    }
  },

  loadAnswersFromLocalStorage: (lectureId: string, userId: string) => {
    const testData = loadTestData(lectureId, userId);
    const selectedChoices: Record<number, number | null> = {};
    const submittedQuestions = new Set<number>();

    testData.answers.forEach((answer) => {
      selectedChoices[answer.questionId] = answer.choiceId;
      submittedQuestions.add(answer.questionId);
    });

    set({
      selectedChoices,
      submittedQuestions,
      lectureId,
    });
  },

  /**
   * APIから取得した回答履歴を selectedChoices に反映する
   * @param answers 回答履歴配列（question_id と choice_num のペア）
   * @param questionIdMap question_id から questionId へのマッピング（通常は同じ）
   */
  loadAnswersFromApi: (
    answers: Array<{ question_id: number; choice_num: number }>,
    questionIdMap: Record<number, number>,
  ) => {
    const { lectureId, userId } = get();
    const selectedChoices: Record<number, number | null> = {};
    const submittedQuestions = new Set<number>();

    console.log('[TestStore] APIから回答データを読み込み:', {
      answersCount: answers.length,
      answers,
      questionIdMap,
    });

    answers.forEach((answer) => {
      // question_id から questionId を取得（通常は同じ値）
      const questionId = questionIdMap[answer.question_id] || answer.question_id;
      if (questionId !== undefined) {
        // choice_num が 0 の場合は未回答（null）
        const choiceId = answer.choice_num === 0 ? null : answer.choice_num;
        selectedChoices[questionId] = choiceId;
        submittedQuestions.add(questionId);
        
        // ローカルストレージにも保存（講義終了後のリロード時に正しく表示されるため）
        if (lectureId && userId) {
          saveTestAnswer(lectureId, userId, questionId, choiceId);
        }
      } else {
        console.warn('[TestStore] question_id に対応する questionId が見つかりません:', answer.question_id);
      }
    });

    console.log('[TestStore] API回答データ読み込み完了:', {
      selectedChoices,
      submittedQuestionsCount: submittedQuestions.size,
    });

    set((state) => ({
      selectedChoices: {
        ...state.selectedChoices,
        ...selectedChoices,
      },
      submittedQuestions: new Set([...state.submittedQuestions, ...submittedQuestions]),
    }));
  },

  /**
   * サーバーの回答履歴を正として、ストアと localStorage の回答を突き合わせる（サーバーが正）。
   *
   * 受講生が画面を閉じている間に管理画面で「中止（回答リセット）」されると、
   * TestCancelled イベントを受信できず localStorage に削除済みの旧回答が残る。
   * サーバーの回答履歴に存在しない問題のローカル回答を削除することで、
   * 再出題後に旧回答（正解表示など）が復活するのを防ぐ。
   */
  reconcileAnswersWithServer: (
    answers: Array<{ question_id: number; choice_num: number }>,
    questionIdMap: Record<number, number>,
    lectureQuestionIds: number[],
  ) => {
    const { lectureId, userId } = get();

    // サーバーに回答が存在する questionId の集合
    const serverQuestionIds = new Set<number>();
    answers.forEach((answer) => {
      const questionId = questionIdMap[answer.question_id] || answer.question_id;
      if (questionId !== undefined) {
        serverQuestionIds.add(questionId);
      }
    });

    // サーバーに存在しない問題のローカル回答を削除
    const staleQuestionIds = lectureQuestionIds.filter((id) => !serverQuestionIds.has(id));
    if (staleQuestionIds.length > 0) {
      console.log('[TestStore] サーバーに回答が存在しないためローカルの旧回答を削除:', staleQuestionIds);
      staleQuestionIds.forEach((questionId) => {
        if (lectureId && userId) {
          removeTestAnswer(lectureId, userId, questionId);
        }
      });
      set((state) => {
        const newSubmitted = new Set(state.submittedQuestions);
        const newChoices = { ...state.selectedChoices };
        staleQuestionIds.forEach((questionId) => {
          newSubmitted.delete(questionId);
          delete newChoices[questionId];
        });
        return {
          submittedQuestions: newSubmitted,
          selectedChoices: newChoices,
        };
      });
    }

    // サーバーの回答をストア・localStorage に反映
    if (answers.length > 0) {
      get().loadAnswersFromApi(answers, questionIdMap);
    }
  },

  saveAnswerToLocalStorage: (lectureId: string, userId: string, questionId: number, choiceId: number | null) => {
    saveTestAnswer(lectureId, userId, questionId, choiceId);
  },

  isQuestionSubmitted: (questionId: number): boolean => {
    return get().submittedQuestions.has(questionId);
  },

  getSelectedChoice: (questionId: number): number | null => {
    const choice = get().selectedChoices[questionId];
    return choice !== undefined ? choice : null;
  },

  clearAnswers: () => {
    set({
      selectedChoices: {},
      submittedQuestions: new Set<number>(),
      answerSendStates: {},
    });
  },

  resetQuestion: (questionId: number) => {
    const { lectureId, userId } = get();
    
    set((state) => {
      const newSubmitted = new Set(state.submittedQuestions);
      newSubmitted.delete(questionId);
      const { [questionId]: _removed, ...restChoices } = state.selectedChoices;
      const { [questionId]: _removedTime, ...restStartTimes } = state.answerStartTimes;
      const { [questionId]: _removedSendState, ...restSendStates } = state.answerSendStates;
      const { [questionId]: _removedDeadline, ...restDeadlines } = state.questionDeadlines;
      const { [questionId]: _removedTimeLimit, ...restTimeLimits } = state.questionTimeLimits;
      return {
        submittedQuestions: newSubmitted,
        selectedChoices: restChoices,
        answerStartTimes: restStartTimes,
        answerSendStates: restSendStates,
        questionDeadlines: restDeadlines,
        questionTimeLimits: restTimeLimits,
        // 世代をインクリメントし、進行中の送信リトライの結果を無効化する（受講生B-8）
        answerEpochs: {
          ...state.answerEpochs,
          [questionId]: (state.answerEpochs[questionId] ?? 0) + 1,
        },
      };
    });

    // localStorage からも該当問題の回答を削除（再出題時に回答済み復元されるのを防ぐ）
    if (lectureId && userId) {
      removeTestAnswer(lectureId, userId, questionId);
    }
  },

  startAnswerTimer: (questionId: number) => {
    set((state) => ({
      answerStartTimes: {
        ...state.answerStartTimes,
        [questionId]: Date.now(),
      },
    }));
  },

  restoreAnswerStartTime: (questionId: number, startTimeMs: number) => {
    set((state) => {
      // WS イベント経由（QuestionOpened）で既に計測開始済みの場合は上書きしない
      if (state.answerStartTimes[questionId] !== undefined) {
        return {};
      }
      return {
        answerStartTimes: {
          ...state.answerStartTimes,
          [questionId]: startTimeMs,
        },
      };
    });
  },

  setRealtimeSubmitFn: (fn: SubmitAnswerFn | null) => {
    set({ realtimeSubmitFn: fn });
  },

  setCheckAnswerableFn: (fn: CheckAnswerableFn | null) => {
    set({ checkAnswerableFn: fn });
  },

  setFetchServerAnswerFn: (fn: FetchServerAnswerFn | null) => {
    set({ fetchServerAnswerFn: fn });
  },

  setUserId: (userId: string) => {
    set({ userId });
  },

  setErrorMessage: (message: string | null) => {
    // 前のクリアタイマーが新しいメッセージを早期に消さないよう破棄する
    if (errorMessageTimer !== undefined) {
      clearTimeout(errorMessageTimer);
      errorMessageTimer = undefined;
    }
    set({ errorMessage: message });
    // 3秒後に自動的にエラーメッセージをクリア
    if (message) {
      errorMessageTimer = setTimeout(() => {
        errorMessageTimer = undefined;
        set({ errorMessage: null });
      }, 3000);
    }
  },

  setQuestionNumMap: (questionId: number, questionNum: number, displayNumber: number | null = null) => {
    set((state) => ({
      questionNumMap: {
        ...state.questionNumMap,
        [questionId]: questionNum,
      },
      displayNumberMap: {
        ...state.displayNumberMap,
        [questionId]: displayNumber,
      },
    }));
  },

  restoreAnswerFromLocalStorage: (lectureId: string, userId: string, questionId: number) => {
    const testData = loadTestData(lectureId, userId);
    const answer = testData.answers.find((a) => a.questionId === questionId);

    // choiceId === null（未回答として送信済み）も復元対象にする（STU-L3対応）。
    // 除外するとリロード後に「未送信」扱いに戻り、締切済みの問題へ再回答を
    // 促す表示になってしまう（送信してもサーバー側で拒否される）
    if (answer) {
      set((state) => ({
        selectedChoices: {
          ...state.selectedChoices,
          [questionId]: answer.choiceId,
        },
        submittedQuestions: new Set([...state.submittedQuestions, questionId]),
      }));
    }
  },

  setQuestionChoices: (questionId: number, choices: Array<{ id: number; internal_id: string }>) => {
    set((state) => ({
      questionChoicesMap: {
        ...state.questionChoicesMap,
        [questionId]: choices,
      },
    }));
  },

  reset: () => {
    // storeEpoch はインクリメントして引き継ぐ
    // （リセット前から進行中の送信リトライの結果が、リセット後のストアに反映されるのを防ぐ。受講生B-8）
    set((state) => ({
      ...initialState,
      storeEpoch: state.storeEpoch + 1,
    }));
  },
}));
